import React, { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { API } from "../config/api"
import { THEME } from "../config/theme"
import PasswordRequirements from "../components/PasswordRequirements"
import { isPasswordValid } from "../utils/password"

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token")
  const hasToken = token !== null && token !== ""

  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => navigate("/login"), 2000)
    return () => clearTimeout(timer)
  }, [success, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!hasToken) {
      setError("Missing reset token")
      return
    }
    if (!isPasswordValid(newPassword)) {
      setError("Password must meet all requirements")
      return
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`${API.baseUrl}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token,
          new_password: newPassword,
        }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.detail || "Reset failed")
      }

      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed")
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "44px",
    border: `0.5px solid ${THEME.colors.neutral.light.border}`,
    borderRadius: THEME.borderRadius.md,
    padding: "0 12px",
    fontSize: "15px",
    fontFamily: THEME.fonts.primary,
    boxSizing: "border-box",
    marginBottom: "16px",
    outline: "none",
  }

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: THEME.colors.neutral.light.textPrimary,
    marginBottom: "6px",
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: THEME.colors.brandGradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: THEME.fonts.primary,
      }}
    >
      <div
        style={{
          width: "400px",
          background: THEME.colors.neutral.light.surface,
          border: `0.5px solid ${THEME.colors.neutral.light.border}`,
          borderRadius: THEME.borderRadius.lg,
          padding: "40px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            fontSize: "24px",
            fontWeight: 600,
            color: THEME.colors.neutral.light.textPrimary,
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          Set new password
        </div>

        {!hasToken ? (
          <>
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: "0.5px solid rgba(220, 38, 38, 0.3)",
                padding: "12px",
                borderRadius: THEME.borderRadius.md,
                fontSize: "13px",
                textAlign: "center",
                marginBottom: "16px",
              }}
            >
              Invalid or missing reset token.
              <br />
              Please request a new password reset.
            </div>
            <div style={{ textAlign: "center" }}>
              <Link
                to="/login"
                style={{
                  fontSize: "13px",
                  color: THEME.colors.navy,
                  textDecoration: "none",
                }}
              >
                Back to login
              </Link>
            </div>
          </>
        ) : (
        <form onSubmit={handleSubmit}>
          <label htmlFor="new" style={labelStyle}>New password</label>
          <input
            id="new"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={inputStyle}
            autoComplete="new-password"
            minLength={8}
            required
            disabled={success}
          />

          <label htmlFor="confirm" style={labelStyle}>Confirm new password</label>
          <input
            id="confirm"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={inputStyle}
            autoComplete="new-password"
            required
            disabled={success}
          />

          <PasswordRequirements password={newPassword} />

          {(() => {
            const canSubmit =
              !loading &&
              !success &&
              isPasswordValid(newPassword) &&
              newPassword === confirmPassword
            return (
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  width: "100%",
                  height: "44px",
                  background: THEME.colors.navy,
                  color: THEME.colors.white,
                  fontWeight: 500,
                  fontSize: "14px",
                  border: "none",
                  borderRadius: THEME.borderRadius.md,
                  marginTop: "8px",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  opacity: canSubmit ? 1 : 0.7,
                  fontFamily: THEME.fonts.primary,
                }}
              >
                {loading ? "Saving..." : "Reset password"}
              </button>
            )
          })()}

          {success && (
            <div
              style={{
                background: "rgba(5, 150, 105, 0.08)",
                color: THEME.colors.semantic.success,
                border: "0.5px solid rgba(5, 150, 105, 0.3)",
                padding: "10px 12px",
                borderRadius: THEME.borderRadius.md,
                marginTop: "12px",
                fontSize: "13px",
                textAlign: "center",
              }}
            >
              Password reset successfully. Redirecting to login...
            </div>
          )}

          {error && !success && (
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: "0.5px solid rgba(220, 38, 38, 0.3)",
                padding: "10px 12px",
                borderRadius: THEME.borderRadius.md,
                marginTop: "12px",
                fontSize: "13px",
              }}
            >
              {error}
            </div>
          )}
        </form>
        )}
      </div>
    </div>
  )
}

export default ResetPasswordPage
